import React, { useState, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import { InstagramTarget, InstagramAccount } from '../../types';
import { translations } from '../../translations';
import { hikerApi } from '../../services/hikerApi';
import { supabase } from '../../lib/supabase';
import { imageService } from '../../services/imageService';
import { validateInstagramUsername } from '../../utils/validation';
import { QualityScore } from '../../components/Targets/QualityScore';
import { SearchBar } from '../../components/Targets/SearchBar';
import { calculateTargetQuality, getQualityColor } from '../../utils/targetQuality';

interface TargetsProps {
  account: InstagramAccount;
  suggestedTargets: InstagramTarget[];
  selectedTargets: InstagramTarget[];
  setSelectedTargets: (targets: InstagramTarget[]) => void;
  isDarkMode: boolean;
  language: string;
}

export function Targets({ 
  account, 
  suggestedTargets, 
  selectedTargets, 
  setSelectedTargets, 
  isDarkMode,
  language
}: TargetsProps) {
  const [targetSearchQuery, setTargetSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const t = translations[language as keyof typeof translations];

  useEffect(() => {
    loadTargets();
  }, [account.id]);

  const loadTargets = async () => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        setError('User not authenticated');
        return;
      }
      
      // Load targets for this account
      const { data: targets, error } = await supabase
        .from('instagram_targets')
        .select('*')
        .eq('account_id', account.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setSelectedTargets(targets || []);
    } catch (err) {
      console.error('Error loading targets:', err);
      setError('Failed to load targets');
    }
  };

  const handleTargetSearchChange = (value: string) => {
    setTargetSearchQuery(value);
    setError(null);
    if (value && !validateInstagramUsername(value)) {
      setUsernameError(t.invalid_username_format);
    } else {
      setUsernameError(null);
    }
  };

  // Format number with spaces
  const formatNumber = (value: string | number) => {
    const num = typeof value === 'string' ? parseInt(value.replace(/[^0-9]/g, ''), 10) : value;
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  const handleAddTarget = async (username: string) => {
    try {
      if (!validateInstagramUsername(username)) {
        setError(t.invalid_username_format);
        return;
      }

      setIsLoading(true);
      setError(null);

      const cleanUsername = username.replace('@', '').toLowerCase().trim();
      if (!cleanUsername) {
        setError('Please enter a valid username');
        return;
      }

      const result = await hikerApi.getAccountInfo(cleanUsername);
      
      if (!result || result.error || !result.data) {
        setError(result?.message || 'Failed to fetch account information');
        return;
      }

      if (!result.data.username) {
        setError('Invalid username received from API');
        return;
      }

      const formattedUsername = '@' + result.data.username.toLowerCase().trim();

      const targetExists = selectedTargets.some(target => 
        target.username.toLowerCase() === formattedUsername
      );

      if (targetExists) {
        setError('This target is already in your list');
        return;
      }

      let avatarUrl = result.data.profilePicUrl || '';
      if (avatarUrl) {
        try {
          const newUrl = await imageService.uploadAvatar(avatarUrl, formattedUsername);
          if (newUrl !== 'https://via.placeholder.com/150') {
            avatarUrl = newUrl;
          }
        } catch (error) {
          console.error('Error uploading avatar:', error);
          avatarUrl = 'https://via.placeholder.com/150';
        }
      }

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        setError('User not authenticated');
        return;
      }

      const newTarget = {
        name: result.data.fullName || cleanUsername,
        username: formattedUsername,
        avatar: avatarUrl,
        followers: result.data.followers?.toString() || '0',
        following: result.data.following?.toString() || '0',
        posts: result.data.posts || 0,
        user_id: userData.user.id,
        account_id: account.id
      };

      const { data, error: dbError } = await supabase
        .from('instagram_targets')
        .insert(newTarget)
        .select()
        .single();

      if (dbError) {
        if (dbError.code === '23505') {
          setError('This target is already in your list');
          return;
        }
        throw dbError;
      }

      if (!data) {
        throw new Error('No data returned from insert operation');
      }

      // Send webhook notification for target addition
      try {
        await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/target-webhook`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            action: 'add',
            targetUsername: formattedUsername,
            accountUsername: account.username
          }),
        });
      } catch (webhookError) {
        console.error('Failed to send webhook notification:', webhookError);
      }

      // Add the new target to the existing list
      setSelectedTargets(prevTargets => [{ ...newTarget, id: data.id }, ...prevTargets]);
      setTargetSearchQuery('');
    } catch (err) {
      console.error('Error adding target:', err);
      setError('Failed to add target. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveTarget = async (targetId: string) => {
    try {
      const targetToRemove = selectedTargets.find(target => target.id === targetId);
      if (!targetToRemove) {
        console.error('Target not found:', targetId);
        return;
      }

      const { error: dbError } = await supabase
        .from('instagram_targets')
        .delete()
        .eq('id', targetId);

      if (dbError) {
        console.error('Error removing target:', dbError);
        setError('Failed to remove target');
        return;
      }

      // Send webhook notification for target removal
      try {
        await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/target-webhook`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            action: 'remove',
            targetUsername: targetToRemove.username,
            accountUsername: account.username
          }),
        });
      } catch (webhookError) {
        console.error('Failed to send webhook notification:', webhookError);
      }

      setSelectedTargets(prevTargets => prevTargets.filter(target => target.id !== targetId));
    } catch (err) {
      console.error('Error removing target:', err);
      setError('Failed to remove target');
    }
  };

  const handleImageError = async (e: React.SyntheticEvent<HTMLImageElement>, target: InstagramTarget) => {
    const imgElement = e.target as HTMLImageElement;
    const originalSrc = imgElement.src;

    if (originalSrc === 'https://via.placeholder.com/150') {
      return;
    }

    imgElement.src = 'https://via.placeholder.com/150';

    try {
      const newUrl = await imageService.handleImageError(originalSrc, target.username);
      
      if (newUrl && newUrl !== 'https://via.placeholder.com/150') {
        imgElement.src = newUrl;

        const { error: updateError } = await supabase
          .from('instagram_targets')
          .update({ avatar: newUrl })
          .eq('id', target.id);

        if (updateError) {
          console.error('Failed to update target avatar:', updateError);
        }
      }
    } catch (error) {
      console.error('Failed to handle image error:', error);
    }
  };

  const handleUsernameKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && targetSearchQuery.trim() && !isLoading) {
      e.preventDefault();
      handleAddTarget(targetSearchQuery);
      return;
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 text-red-500 p-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <QualityScore targets={selectedTargets} isDarkMode={isDarkMode} language={language} />

      <div className={`p-6 rounded-lg border ${
        isDarkMode 
          ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
          : 'bg-white border-[#e5e7eb]'
      }`}>
        <h2 className={`text-lg font-semibold mb-4 ${
          isDarkMode ? 'text-gray-200' : 'text-gray-900'
        }`}>{t.search_targets}</h2>
        <SearchBar
          searchQuery={targetSearchQuery}
          onSearchChange={handleTargetSearchChange}
          onAdd={handleAddTarget}
          isLoading={isLoading}
          isDarkMode={isDarkMode}
          language={language}
          onKeyPress={handleUsernameKeyPress}
        />
      </div>

      {selectedTargets.length > 0 && (
        <div className={`p-6 rounded-lg border ${
          isDarkMode 
            ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
            : 'bg-white border-[#e5e7eb]'
        }`}>
          <h2 className={`text-lg font-semibold mb-4 ${
            isDarkMode ? 'text-gray-200' : 'text-gray-900'
          }`}>{t.selected_targets}</h2>
          <div className="space-y-4">
            {selectedTargets.map(target => {
              const qualityScore = calculateTargetQuality(target);
              const qualityColor = getQualityColor(qualityScore);
              
              return (
                <div key={target.id} className={`flex items-center gap-4 p-3 rounded-lg group ${
                  isDarkMode ? 'bg-[#2b2b2b]' : 'bg-gray-50'
                }`}>
                  <img
                    src={target.avatar || 'https://via.placeholder.com/150'}
                    alt={target.name}
                    className="w-12 h-12 rounded-full object-cover"
                    onError={(e) => handleImageError(e, target)}
                    loading="lazy"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className={`text-[13px] font-medium ${
                        isDarkMode ? 'text-gray-200' : 'text-gray-900'
                      }`}>{target.name}</div>
                      <div className={`text-[12px] ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>{target.username}</div>
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-16 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${qualityColor.bg} transition-all`}
                            style={{ width: `${qualityScore}%` }}
                          />
                        </div>
                        <span className={`text-[11px] font-medium ${qualityColor.text}`}>
                          {qualityScore}%
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-1">
                      <div className={`text-[12px] ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        <span className={`font-medium ${
                          isDarkMode ? 'text-gray-200' : 'text-gray-900'
                        }`}>{formatNumber(target.followers)}</span> {t.followers}
                      </div>
                      <div className={`text-[12px] ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        <span className={`font-medium ${
                          isDarkMode ? 'text-gray-200' : 'text-gray-900'
                        }`}>{formatNumber(target.following)}</span> {t.following}
                      </div>
                      <div className={`text-[12px] ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        <span className={`font-medium ${
                          isDarkMode ? 'text-gray-200' : 'text-gray-900'
                        }`}>{formatNumber(target.posts)}</span> {t.posts}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveTarget(target.id)}
                    className="text-gray-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <Trash2 size={15} strokeWidth={2.3} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}